import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, Users, DollarSign, CalendarOff, MessageSquare, TrendingUp, Download, MapPin } from 'lucide-react';

export default function Reports() {
  const { api } = useAuth();
  const [report, setReport] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationKpis, setLocationKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const [rRes, lRes] = await Promise.all([
          api().get('/api/reports/monthly'),
          api().get('/api/locations'),
        ]);
        setReport(rRes.data);
        setLocations(lRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [api]);

  useEffect(() => {
    if (!selectedLocation) { setLocationKpis(null); return; }
    async function loadLocation() {
      try {
        const { data } = await api().get(`/api/dashboard/kpis-by-location?location_id=${selectedLocation}`);
        setLocationKpis(data);
      } catch (e) { console.error(e); }
    }
    loadLocation();
  }, [selectedLocation, api]);

  const exportCsv = async (type) => {
    setExporting(type);
    try {
      const endpoints = {
        leads: '/api/reports/export/leads',
        monthly: '/api/reports/export/monthly',
        'no-shows': '/api/reports/export/no-shows',
      };
      const response = await api().get(endpoints[type], { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setExporting('');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const metrics = report ? [
    { label: 'New Leads', value: report.new_leads, icon: Users, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Won Leads', value: report.won_leads, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Revenue', value: `$${(report.revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-slate-900', bg: 'bg-slate-100' },
    { label: 'Appointments', value: report.total_appointments, icon: BarChart3, color: 'text-violet-500', bg: 'bg-violet-50' },
    { label: 'No Shows', value: report.no_shows, icon: CalendarOff, color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'No-Show Rate', value: `${(report.no_show_rate || 0).toFixed(1)}%`, icon: CalendarOff, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: 'SMS Sent', value: report.sms_sent, icon: MessageSquare, color: 'text-sky-500', bg: 'bg-sky-50' },
  ] : [];

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">{report?.period || 'Current Month'}</p>
        </div>
        <div className="flex items-center gap-2">
          {locations.length > 1 && (
            <select
              data-testid="reports-location-filter"
              value={selectedLocation}
              onChange={e => setSelectedLocation(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white"
            >
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Monthly Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map(m => (
          <div key={m.label} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className={`w-9 h-9 ${m.bg} rounded-lg flex items-center justify-center mb-3`}>
              <m.icon size={18} className={m.color} />
            </div>
            <p className="text-2xl font-bold text-slate-900 tracking-tight">{m.value}</p>
            <p className="text-xs text-slate-500 mt-1">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Location Breakdown */}
      {locationKpis && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <MapPin size={16} /> {locationKpis.location?.location_name} Breakdown
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500">Total Leads</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{locationKpis.total_leads}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500">New Leads (30d)</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{locationKpis.new_leads_30d}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500">Won Revenue</p>
              <p className="text-xl font-bold text-slate-900 mt-1">${(locationKpis.won_revenue || 0).toLocaleString()}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500">No Shows</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{locationKpis.no_shows}</p>
            </div>
          </div>
        </div>
      )}

      {/* CSV Export */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Export Reports</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { key: 'leads', label: 'Leads Export', desc: 'All leads with stages, values, and sources' },
            { key: 'monthly', label: 'Monthly Summary', desc: 'Last 6 months of KPIs and revenue' },
            { key: 'no-shows', label: 'No-Show Report', desc: 'All no-show appointments with patient info' },
          ].map(r => (
            <button
              key={r.key}
              data-testid={`export-${r.key}`}
              onClick={() => exportCsv(r.key)}
              disabled={exporting === r.key}
              className="text-left p-4 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-2 mb-1">
                <Download size={14} className="text-slate-600" />
                <span className="text-sm font-medium text-slate-900">{r.label}</span>
              </div>
              <p className="text-xs text-slate-500">{r.desc}</p>
              {exporting === r.key && <p className="text-xs text-slate-400 mt-1">Exporting...</p>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
